// M330 — Trang admin "Theo dõi ôn tập từng câu" (2026-08-09)
// Route: /admin/flashcard-tracking (perm 'progress' — cùng quyền trang Tiến trình NV)
//
// - Tab "Từng nhân viên": câu NV đã học + lịch sử từng lần bấm Quên/Khó/Dễ/Siêu dễ
//   (từ flashcard_review_log M329, chỉ có dữ liệu từ 09/08/2026) + lần tới học + đáp án.
// - Tab "Câu cả shop hay Quên/Khó": xếp hạng câu bị bấm Quên/Khó nhiều nhất toàn shop.
// - Nút "Sửa câu hỏi" mở QuestionEditor (popup, M136: chỉ đóng bằng × hoặc Huỷ —
//   đang gõ dở không bị mất) → PATCH /questions/:id.

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import AdminPageHeader from '../components/AdminPageHeader.jsx';
import QuestionEditor from '../components/QuestionEditor.jsx';
import { safeRichHtml, toPlainPreview } from '../lib/renderRichText.js';

const QUALITY = {
  1: { letter: 'Q', label: 'Quên',    cls: 'bg-red-100 text-red-700' },
  2: { letter: 'K', label: 'Khó',     cls: 'bg-amber-100 text-amber-700' },
  3: { letter: 'D', label: 'Dễ',      cls: 'bg-green-100 text-green-700' },
  4: { letter: 'S', label: 'Siêu dễ', cls: 'bg-blue-100 text-blue-700' },
};

// Label theo M327: key DB giữ nguyên, chỉ đổi tên hiển thị
const SET_OPTIONS = [
  { key: '',            label: 'Bộ: Tất cả' },
  { key: 'bo_1000k',    label: '500k' },
  { key: 'bo_1250k',    label: '1000k' },
  { key: 'bo_nang_cao', label: '1250k' },
  { key: 'bo_noi_quy',  label: 'Nội quy' },
];
const setLabel = (key) => SET_OPTIONS.find((s) => s.key === key)?.label || key;

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

// "Hôm nay" / "Ngày mai" / "Còn X ngày" / "Quá hạn X ngày" theo ngày VN
function nextReviewText(iso) {
  if (!iso) return { text: 'Chưa hẹn', cls: 'text-slate-400' };
  const VN_OFFSET = 7 * 3600 * 1000;
  const dayOf = (ms) => Math.floor((ms + VN_OFFSET) / 86400000);
  const diff = dayOf(new Date(iso).getTime()) - dayOf(Date.now());
  if (diff < 0)   return { text: `Quá hạn ${-diff} ngày`, cls: 'text-red-600 font-semibold' };
  if (diff === 0) return { text: 'Hôm nay',               cls: 'text-red-600 font-semibold' };
  if (diff === 1) return { text: 'Ngày mai',              cls: 'text-amber-600 font-semibold' };
  return { text: `Còn ${diff} ngày`, cls: 'text-slate-700' };
}

function QualityDot({ quality, title }) {
  const meta = QUALITY[quality];
  if (!meta) return null;
  return (
    <span
      title={title || meta.label}
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${meta.cls}`}
    >
      {meta.letter}
    </span>
  );
}

function HistoryDots({ history, lastQuality }) {
  if (!history.length) {
    // Học trước 09/08 — chưa có nhật ký, chỉ biết lần bấm gần nhất
    return (
      <span className="flex items-center gap-1">
        {lastQuality ? <QualityDot quality={lastQuality} title="Lần bấm gần nhất (trước khi có nhật ký)" /> : null}
        <span className="text-xs text-slate-400">chưa có nhật ký</span>
      </span>
    );
  }
  const MAX = 15;
  const shown = history.slice(-MAX);
  return (
    <span className="flex flex-wrap items-center gap-0.5">
      {history.length > MAX && <span className="mr-1 text-xs text-slate-400">+{history.length - MAX}</span>}
      {shown.map((h, i) => (
        <QualityDot key={i} quality={h.quality} title={`${fmtDateTime(h.reviewed_at)} — ${QUALITY[h.quality]?.label}`} />
      ))}
    </span>
  );
}

function MetricCard({ label, value, sub, tone }) {
  const toneCls = tone === 'danger' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-slate-800';
  return (
    <div className="rounded-xl border bg-white px-4 py-3">
      <div className="text-[13px] text-slate-500">{label}</div>
      <div className={`mt-0.5 text-2xl font-semibold ${toneCls}`}>
        {value}{sub ? <span className="ml-1 text-sm font-normal text-slate-400">{sub}</span> : null}
      </div>
    </div>
  );
}

export default function AdminFlashcardTrackingPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [boFilter, setBoFilter] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState('nv'); // 'nv' | 'stats'
  const [statsDays, setStatsDays] = useState('30');
  const [statsBo, setStatsBo] = useState('');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [expanded, setExpanded] = useState({});        // card_id -> bool (tab NV)
  const [expandedStats, setExpandedStats] = useState({});
  const [editor, setEditor] = useState({ open: false, initial: null });

  // Danh sách NV active cho dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await api.adminEmployees();
        const active = (r.employees || []).filter((e) => e.status === 'active');
        setEmployees(active);
        if (active.length && !employeeId) setEmployeeId(String(active[0].id));
      } catch (err) {
        toast.error(err?.data?.error || err?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployee = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const r = await api.flashcardTrackingEmployee(employeeId, { bo: boFilter || undefined });
      setData(r);
      setExpanded({});
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId, boFilter]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const r = await api.flashcardTrackingStats({ days: statsDays, bo: statsBo || undefined });
      setStats(r);
      setExpandedStats({});
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
    } finally {
      setLoadingStats(false);
    }
  }, [statsDays, statsBo]);

  useEffect(() => { if (tab === 'stats') loadStats(); }, [tab, loadStats]);

  // Lọc theo ô tìm kiếm ngay trên client (gõ tiếng Việt không cần Enter)
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const s = search.trim().toLowerCase();
    if (!s) return all;
    return all.filter((r) => toPlainPreview(r.question).toLowerCase().includes(s));
  }, [data, search]);

  function openEditor(row) {
    setEditor({
      open: true,
      initial: {
        id: row.card_id,
        bo: row.question_bo || row.bo,
        topic: row.topic,
        question: row.question,
        answer: row.answer,
      },
    });
  }

  async function handleEditorSave(payload) {
    const id = editor.initial?.id;
    try {
      await api.questionUpdate(id, {
        question: payload.question,
        answer: payload.answer,
        topic: payload.topic,
      });
      toast.success('Đã lưu câu hỏi');
    } catch (err) {
      toast.error(err?.data?.error || err?.message);
      throw err; // giữ editor mở khi lỗi
    }
    setEditor({ open: false, initial: null });
    loadEmployee();
    if (tab === 'stats' || stats) loadStats();
  }

  const summary = data?.summary;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16">
      <AdminPageHeader
        title="🔎 Theo dõi ôn tập từng câu"
        subtitle="Xem NV đang nhớ hay quên câu nào — và câu nào cả shop hay sai để sửa lại"
      />

      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
        Nhật ký từng lần bấm được ghi từ <b>09/08/2026</b>. Các lần ôn trước ngày này chỉ hiện được lần bấm gần nhất.
      </div>

      {/* Bộ lọc */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          {!employees.length && <option value="">Chưa có NV active</option>}
          {employees.map((e) => (
            <option key={e.id} value={e.id}>NV: {e.full_name}</option>
          ))}
        </select>
        <select
          value={boFilter}
          onChange={(e) => setBoFilter(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          {SET_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tìm câu hỏi..."
          className="w-48 rounded-lg border px-3 py-2 text-sm"
        />
      </div>

      {/* 4 ô số liệu */}
      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <MetricCard label="Câu đã học" value={summary.learned} sub={`/ ${summary.total_active}`} />
          <MetricCard label="Lượt ôn (từ 09/08)" value={summary.total_reviews_logged} />
          <MetricCard label="Đến hạn hôm nay" value={summary.due_today} tone="warn" />
          <MetricCard label="Câu hay quên" value={summary.hay_quen} tone="danger" />
        </div>
      )}

      {/* Tabs */}
      <div className="mb-3 flex gap-1 border-b">
        <button
          onClick={() => setTab('nv')}
          className={`rounded-t-lg px-4 py-2 text-sm ${tab === 'nv' ? 'border border-b-white bg-white font-semibold text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Từng nhân viên
        </button>
        <button
          onClick={() => setTab('stats')}
          className={`rounded-t-lg px-4 py-2 text-sm ${tab === 'stats' ? 'border border-b-white bg-white font-semibold text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Câu cả shop hay Quên / Khó
        </button>
      </div>

      {/* ===== Tab 1: từng NV ===== */}
      {tab === 'nv' && (
        <div className="rounded-xl border bg-white">
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-[13px] text-slate-500">
            Lịch sử bấm (cũ → mới):
            <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">Q = Quên</span>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">K = Khó</span>
            <span className="rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-700">D = Dễ</span>
            <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700">S = Siêu dễ</span>
            <span className="ml-1">· Bấm vào hàng để xem đáp án + chi tiết</span>
          </div>

          {loading && <div className="px-4 py-8 text-center text-sm text-slate-400">Đang tải...</div>}
          {!loading && !rows.length && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              {data ? 'NV chưa học câu nào trong phạm vi lọc này' : 'Chọn nhân viên để xem'}
            </div>
          )}

          {!loading && rows.map((r) => {
            const next = nextReviewText(r.next_review_at);
            const isOpen = !!expanded[r.card_id];
            return (
              <div key={r.card_id} className={r.hay_quen ? 'bg-red-50/40' : ''}>
                <div
                  onClick={() => setExpanded((p) => ({ ...p, [r.card_id]: !p[r.card_id] }))}
                  className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-2 border-t px-4 py-2.5 text-sm hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_52px_170px_110px]"
                >
                  <span className="font-medium text-slate-800">
                    {toPlainPreview(r.question)}
                    {r.hay_quen && (
                      <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Hay quên</span>
                    )}
                    <span className="ml-2 text-xs text-slate-400">{setLabel(r.bo)}</span>
                  </span>
                  <span className="hidden text-center text-slate-600 md:block">{r.review_count || '—'}</span>
                  <span className="hidden md:block"><HistoryDots history={r.history} lastQuality={r.last_quality} /></span>
                  <span className={`text-right text-[13px] md:text-left ${next.cls}`}>{next.text}</span>
                </div>

                {isOpen && (
                  <div className="border-t border-dashed bg-slate-50 px-4 py-3 text-sm">
                    <div className="md:hidden mb-2"><HistoryDots history={r.history} lastQuality={r.last_quality} /></div>
                    <div className="rounded-r-lg border border-l-4 border-l-green-600 bg-white px-3 py-2">
                      <div className="mb-1 text-xs font-semibold text-green-700">Đáp án:</div>
                      <div className="rich-content leading-relaxed" dangerouslySetInnerHTML={safeRichHtml(r.answer)} />
                    </div>
                    {r.history.length > 0 && (
                      <div className="mt-2 leading-7 text-slate-600">
                        <b>Từng lần ôn:</b>{' '}
                        {r.history.map((h, i) => (
                          <span key={i}>
                            {i > 0 && ' · '}
                            {fmtDateTime(h.reviewed_at)} — {QUALITY[h.quality]?.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                      <span>Ôn gần nhất: {fmtDateTime(r.last_review_at)}</span>
                      <span>Hẹn ôn: {fmtDateTime(r.next_review_at)}</span>
                      {r.topic && <span>Chủ đề: {r.topic}</span>}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditor(r); }}
                      className="mt-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-100"
                    >
                      Sửa câu hỏi
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Tab 2: thống kê toàn shop ===== */}
      {tab === 'stats' && (
        <div className="rounded-xl border bg-white">
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
            <select value={statsDays} onChange={(e) => setStatsDays(e.target.value)} className="rounded-lg border px-3 py-1.5 text-sm">
              <option value="7">7 ngày qua</option>
              <option value="30">30 ngày qua</option>
              <option value="all">Từ đầu (09/08)</option>
            </select>
            <select value={statsBo} onChange={(e) => setStatsBo(e.target.value)} className="rounded-lg border px-3 py-1.5 text-sm">
              {SET_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <span className="text-[13px] text-slate-500">Câu bị bấm Quên/Khó nhiều xếp trên. Bấm hàng xem đáp án, bấm Sửa để chỉnh.</span>
          </div>

          <div className="hidden grid-cols-[minmax(0,1fr)_60px_56px_56px_130px_70px] gap-2 px-4 py-2 text-xs text-slate-500 md:grid">
            <span>Câu hỏi</span><span className="text-center">NV học</span>
            <span className="text-center">Quên</span><span className="text-center">Khó</span>
            <span>Tỷ lệ Quên+Khó</span><span></span>
          </div>

          {loadingStats && <div className="px-4 py-8 text-center text-sm text-slate-400">Đang tải...</div>}
          {!loadingStats && !(stats?.rows || []).length && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Chưa có dữ liệu — nhật ký chỉ ghi từ 09/08/2026, NV ôn tập tới đâu số liệu hiện tới đó
            </div>
          )}

          {!loadingStats && (stats?.rows || []).map((r) => {
            const isOpen = !!expandedStats[r.card_id];
            return (
              <div key={r.card_id}>
                <div
                  onClick={() => setExpandedStats((p) => ({ ...p, [r.card_id]: !p[r.card_id] }))}
                  className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-2 border-t px-4 py-2.5 text-sm hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_60px_56px_56px_130px_70px]"
                >
                  <span className="font-medium text-slate-800">
                    {toPlainPreview(r.question)}
                    <span className="ml-2 text-xs text-slate-400">{setLabel(r.bo)}</span>
                  </span>
                  <span className="hidden text-center text-slate-600 md:block">{r.nv_count}</span>
                  <span className="hidden text-center font-semibold text-red-600 md:block">{r.quen}</span>
                  <span className="hidden text-center font-semibold text-amber-600 md:block">{r.kho}</span>
                  <span className="hidden items-center gap-2 md:flex">
                    <span className="h-2 flex-1 rounded-full bg-slate-100">
                      <span className="block h-2 rounded-full bg-red-500" style={{ width: `${r.rate_pct}%` }} />
                    </span>
                    <span className="w-9 text-xs text-slate-500">{r.rate_pct}%</span>
                  </span>
                  <span className="text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditor(r); }}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                    >
                      Sửa câu
                    </button>
                  </span>
                </div>
                {isOpen && (
                  <div className="border-t border-dashed bg-slate-50 px-4 py-3 text-sm">
                    <div className="md:hidden mb-2 text-xs text-slate-500">
                      {r.nv_count} NV học · <span className="font-semibold text-red-600">{r.quen} Quên</span> · <span className="font-semibold text-amber-600">{r.kho} Khó</span> · {r.rate_pct}% trên {r.total_reviews} lượt
                    </div>
                    <div className="rounded-r-lg border border-l-4 border-l-green-600 bg-white px-3 py-2">
                      <div className="mb-1 text-xs font-semibold text-green-700">Đáp án hiện tại:</div>
                      <div className="rich-content leading-relaxed" dangerouslySetInnerHTML={safeRichHtml(r.answer)} />
                    </div>
                    <div className="mt-2 hidden text-xs text-slate-500 md:block">
                      {r.total_reviews} lượt ôn trong kỳ · tỷ lệ Quên+Khó {r.rate_pct}%
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <QuestionEditor
        open={editor.open}
        mode="edit"
        initial={editor.initial}
        onClose={() => setEditor({ open: false, initial: null })}
        onSave={handleEditorSave}
      />
    </div>
  );
}
